import { type PropsWithChildren, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import ChevronLeft from "@/design-system/icons/chevron-left";
import ChevronRight from "@/design-system/icons/chevron-right";
import { useViewportInfoContext } from "@/src/contexts";
import { type IFlyerCatalogueImage } from "@/src/flyers/FlyerCatalogue/FlyerCatalogueHelpers";
import { FlyerCataloguePage } from "@/src/flyers/FlyerCatalogue/FlyerCataloguePage";
import { useFlyerDetailsContext } from "@/src/flyers/FlyerDetails/FlyerDetailsContext";
import { flyerPageQueryParam } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { type PropsWithStyleX, type QueryLgMin, colors } from "../../../styles/tokens.stylex";

export interface IFlyersCarousel extends PropsWithChildren<PropsWithStyleX> {
    onMobileSlide?: () => void;
    onPrevClick?: React.MouseEventHandler<HTMLButtonElement>;
    onNextClick?: React.MouseEventHandler<HTMLButtonElement>;
    pages: IFlyerCatalogueImage[];
    flyerId: string;
    onVisible?: (carouselIndex: number) => void;
    hideArrows?: boolean;
}

export default function FlyersCatalogueCarousel(props: IFlyersCarousel) {
    const initialLoading = useRef(true);
    const carouselRef = useRef<HTMLOListElement>();
    const [disabledArrowState, setDisabledArrowsState] = useState({ left: true, right: true });
    const { isMobile, isMobileOrTablet } = useViewportInfoContext();
    const [searchParams] = useSearchParams();
    const isSearchNavigation = !!searchParams.get("navigationKey");
    const { isZoomed } = useFlyerDetailsContext();

    const updateScroll = (left: number, behavior: ScrollBehavior = "smooth") => {
        const { scrollWidth, clientWidth } = carouselRef.current;

        carouselRef.current.scrollTo({ left, behavior });
        const aLeft = left <= 0;
        const aRight = scrollWidth - clientWidth <= left + 5;

        if (aLeft !== disabledArrowState.left || aRight !== disabledArrowState.right) {
            setDisabledArrowsState({ right: aRight, left: aLeft });
        }
    };

    const paramsPageNumber = Number(searchParams.get(flyerPageQueryParam));

    useLayoutEffect(() => {
        let scrollPos = 0;

        if (paramsPageNumber > 0) {
            const page = props.pages.find((page) => page.pageNumber === paramsPageNumber) || props.pages[0];
            const carouselRect = carouselRef.current.getBoundingClientRect();
            const elemRect = carouselRef.current.children[page.carouselPosition].getBoundingClientRect();

            scrollPos = elemRect.left - carouselRect.left + carouselRef.current.scrollLeft;
            updateScroll(scrollPos, initialLoading.current ? "instant" : "smooth");
            initialLoading.current = false;
        }
    }, [isMobileOrTablet, isSearchNavigation && paramsPageNumber, isZoomed]);

    const carousel = (
        <ol
            {...stylex.props(styles.carousel, props.styleX)}
            ref={carouselRef}
            onTouchEnd={props.onMobileSlide}
        >
            {props.pages.map((magPage, index) => (
                <FlyerCataloguePage
                    key={index}
                    image={magPage}
                    flyerId={props.flyerId}
                    isMobileOrTablet={isMobileOrTablet}
                    carouselRef={carouselRef}
                    onVisible={props.onVisible}
                />
            ))}
        </ol>
    );

    const onNextClick: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
        if (props.onNextClick) {
            props.onNextClick(ev);
        }
        if (ev.isDefaultPrevented()) {
            return;
        }

        const newScrollPosition = carouselRef.current.scrollLeft + carouselRef.current.clientWidth;
        updateScroll(newScrollPosition);
    };

    const onPrevClick: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
        if (props.onPrevClick) {
            props.onPrevClick(ev);
        }

        if (ev.isDefaultPrevented()) {
            return;
        }

        const newScrollPosition = carouselRef.current.scrollLeft - carouselRef.current.clientWidth;
        updateScroll(newScrollPosition);
    };

    if (!isMobile) {
        return (
            <div {...stylex.props(styles.arrowContainer)}>
                {!props.hideArrows && (
                    <button
                        {...stylex.props(styles.arrow, styles.arrowPrev)}
                        type="button"
                        disabled={disabledArrowState.left}
                        onClick={onPrevClick}
                    >
                        <ChevronLeft />
                    </button>
                )}
                {carousel}
                {!props.hideArrows && (
                    <button
                        {...stylex.props(styles.arrow, styles.arrowNext)}
                        type="button"
                        disabled={disabledArrowState.right}
                        onClick={onNextClick}
                    >
                        <ChevronRight />
                    </button>
                )}
            </div>
        );
    }

    return carousel;
}
const lgMin: QueryLgMin = "@media (min-width: 1240px)";

const arrowOffset = "60px";

const styles = stylex.create({
    carousel: {
        scrollSnapType: "x mandatory",
        display: "flex",
        position: "relative",
        scrollBehavior: "smooth",
        scrollbarWidth: "none",
        willChange: "scroll-position",
        height: "100%",
        overflowX: "scroll",
        overflowY: "hidden",
        width: "100%",

        "::-webkit-scrollbar": { display: "none" },
    },
    arrow: {
        strokeWidth: 2,

        transition: "all 300ms",
        color: colors.fg_AltDefault,
        opacity: 1,
        position: "absolute",
        transform: "translateY(-50%)",
        visibility: {
            default: "visible",
            ":disabled": "hidden",
        },
        zIndex: 100,
        height: 50,
        top: "50%",
        width: 50,
    },
    arrowPrev: {
        left: "0",
    },
    arrowNext: {
        right: "0",
    },
    arrowContainer: {
        margin: {
            default: null,
            [lgMin]: `0 -${arrowOffset}`,
        },
        padding: `0 ${arrowOffset}`,
        position: "relative",
        height: "100%",
    },
});
